import React, { useState, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import  DatePicker  from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, endOfDay, format, isWithinInterval, parseISO, startOfDay, subDays } from "date-fns";
import Banner from "../components/Banner";
import axiosInstance from "../utils/axiosSetup";
import { Spinner } from "@heroui/react";

interface UploadedDoc {
    fileName: string;
    entityType: string;
    entity: string;
    documentType: string;
    authorDate: string;
    documentDate: string | null;
    uploadDate: string; // ISO String
    status: "Success" | "Failed";

}


const StatusPage: React.FC = () => {
    const [allDocs, setAllDocs] = useState<UploadedDoc[]>([]);
    const [filteredDocs, setFilteredDocs] = useState<UploadedDoc[]>([]);

    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
    const [endDate, setEndDate] = useState<Date>(new Date());

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchDocuments();
        

    }, []);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get<UploadedDoc[]>("/uploader/get_uploaded_doc_list");

            console.log("response from report page is ", response);
            if(!Array.isArray(response.data)) {
                throw new Error("Invalid response format");
            }

            const validDocs = response.data.filter(doc => 
                // doc.fileName && doc.uploadDate
                doc.fileName //Keep all docs even if uploadDate is empty
            );

            setAllDocs(validDocs);
            filterDocs(validDocs, startDate, endDate);
            setError(null);
        } catch (err) {
            console.log("Failed to fetch documents", err);
            setError("Failed to fetch documents. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // const filterDocs = (docs: UploadedDoc[], start: Date, end: Date) => {
    //     const filtered = docs.filter((doc) => {
    //         if(!doc.uploadDate) return true; //Include failed docs

    //         return isWithinInterval(parseISO(doc.uploadDate), {
    //             start,
    //             end: addDays(end, 1),
    //         }); 
    //     });
    //         setFilteredDocs(filtered);
    //     };
        
    // filtering docs inclusive of range, including startdate 
    const filterDocs = (docs: UploadedDoc[], start: Date, end: Date) => {
        const filtered = docs.filter((doc) => {
            if (!doc.uploadDate) return true; // Include failed docs
    
            const uploadDate = parseISO(doc.uploadDate);
            return isWithinInterval(uploadDate, {
            start: startOfDay(start),
            end: endOfDay(end),
            });
        });
    
        setFilteredDocs(filtered);
        };

    const handleDateChange = (which: "start" | "end", date: Date | null) => {
        if(!date) return;
        if(which === "start") { 
            setStartDate(date);
            filterDocs(allDocs, date, endDate);
        } else {
            setEndDate(date);
            filterDocs(allDocs, startDate, date);
        }
    };

        //Refetch filtered
    //     filterDocs(allDocs, which === "start" ? date : startDate, which === "end" ? date : endDate);

    // };

    const columnDefs: ColDef[] = useMemo(
        () => [
            {   
                headerName: "File Name", 
                field: "fileName",  
                tooltipField: "fileName", 
                sortable: true,
                minWidth: 320,
                flex:2
            },
            { headerName: "Type", 
                field: "entityType", 
                sortable: true,
                flex:1,
                minWidth: 160

            },
            { 
                headerName: "Entity", 
                field: "entity", 
                sortable: true,
                minWidth: 220,
                flex: 1.5
            },
            { 
                headerName: "Document Type", 
                field: "documentType", 
                sortable: true,
                minWidth: 160,
                flex:1.5
            },
            // { 
            //     headerName: "Author Date", 
            //     field: "authorDate", 
            //     valueFormatter: (params: any) =>
            //     params.value ? format(params.value, "yyyy-MM-dd") : "",
            //     sortable: true,
            //     flex:1,
            //     minWidth:140
            // },

            { 
                headerName: "Document Date",
                 field: "documentDate", 
                 sortable: true,
                 flex:1,
                 minWidth:160,
                 
                 
                
            },
            {
                headerName: "Upload Date",
                field: "uploadDate",
                flex:1,
                minWidth:140,
                valueFormatter: (params: any) =>
                    params.value ? format(parseISO(params.value), "yyyy-MM-dd") : "",
                cellStyle: (params: any) => ({
                    color: params.value ? "inherit" : "red",
                    fontWeight: params.value ? "normal" : "bold",
                })
            },
            {
                headerName: "Status",
                field: "status",
                flex:1,
                minWidth:120,
                cellStyle: (params: any) => ({
                    color: params.value === "Success" ? "green" : "red",
                    fontWeight: "bold",
                }),
            },
        ], []
    );


    return (
        <>
        <Banner current="status" />
       

        <div data-testid="status-page" className="p-6 max-w-screen-xl mx-auto">
            <h1 className="text-2xl font-bold text-start mb-4">
                Uploaded Document Status
            </h1>

            {error && (
                <div className="text-red-600 font-medium mb-4">{error}</div>
            )}


            <div className="flex gap-4 items-end flex-wrap mb-6">
                <div>
                    <label className="block mb-1 font-medium text-sm text-gray-700">
                        Start Date
                    </label>
                    <DatePicker
                        selected={startDate}
                        onChange={(d) => handleDateChange("start", d)}
                        maxDate={endDate}
                        className="border px-3 py-2 rounded w-[200px]"
                        dateFormat = "yyyy-MM-dd"
                    />
                </div>

                <div>
                    <label className="block mb-1 font-medium text-sm text-gray-700">
                        End Date
                    </label>
                    <DatePicker
                        selected={endDate}
                        onChange={(d) => handleDateChange("end", d)}
                        minDate={startDate}
                        maxDate={new Date()}
                        className="border px-3 py-2 rounded w-[200px]"
                        dateFormat = "yyyy-MM-dd"
                    />
                </div>
            </div>

            {loading && (
                 <div className="m-4">

                 <div className="flex flex-col items-center gap-2 text-blue-600 text-md">
                     <Spinner size="lg" color="secondary" label="Loading documents..." />
                 </div>
             </div>
                // <div className="text-center text-lg font-medium">Loading Documents...</div>
            )}

            <div data-testid="status-grid" className="ag-theme-alpine w-full h-[600px]">
                <AgGridReact
                    rowData={filteredDocs}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={20}
                    domLayout="autoHeight"
                />

            </div>
            
        </div>

    
        
        </>
    )
}


export default StatusPage;